import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import useAuth from "../../hooks/useAuth";
import CompareSkeleton from "./compareSkeleton";
import ErrorDisplay from "../common/error";
import {
  getBranches as firstGetBranches,
  getReports as firstGetAllReports,
  getReport as firstGetReportDetails,
  resetFirstCompareGetBranches,
  resetFirstCompareGetReports,
  resetFirstCompareGetReportDetails,
} from "../../redux/compare/firstCompare";

import {
  getBranches as secondGetBranches,
  getReports as secondGetAllReports,
  getReport as secondGetReportDetails,
  resetSecondCompareGetBranches,
  resetSecondCompareGetReports,
  resetSecondCompareGetReportDetails,
} from "../../redux/compare/secondCompare";

import { extractIp, formatDate, formatToDate } from "../../redux/utils";
import MyTable from "../common/table";
import TableSkeleton from "../common/tableSkeleton";
//import CustomInput from "../common/customInput";
import { filterReportTable, isEmpty } from "../../utils/filterReportTable";
import DateTimePicker from "../common/dateTimePicker";
import { useTranslation } from "react-i18next";
import CompareSelect from "../common/compareSelect";
import CustomSelect from "../common/customSelect";

const CompareForm = ({
  index,
  setDataLeft,
  setDataRight,
  setLeftBranch,
  setRightBranch,
}) => {
  const { t } = useTranslation();

  const {
    getAllBranches,
    getAllReports,
    getReportDetails,
    resetGetBranches,
    resetGetReports,
    resetGetReportDetails,
  } = useMemo(() => {
    if (index === 1) {
      return {
        getAllBranches: firstGetBranches,
        getAllReports: firstGetAllReports,
        getReportDetails: firstGetReportDetails,
        resetGetBranches: resetFirstCompareGetBranches,
        resetGetReports: resetFirstCompareGetReports,
        resetGetReportDetails: resetFirstCompareGetReportDetails,
      };
    } else {
      return {
        getAllBranches: secondGetBranches,
        getAllReports: secondGetAllReports,
        getReportDetails: secondGetReportDetails,
        resetGetBranches: resetSecondCompareGetBranches,
        resetGetReports: resetSecondCompareGetReports,
        resetGetReportDetails: resetSecondCompareGetReportDetails,
      };
    }
  }, [index]);

  const dispatch = useDispatch();
  const auth = useAuth().auth();
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // const [searchQuery, setSearchQuery] = useState("");

  const formatBranches = (branches) => {
    return branches.map((branch) => ({
      value: extractIp(branch.ipAddress),
      label: branch.name,
      ipAddress: branch.ipAddress,
    }));
  };

  const formatReports = (reports) => {
    return reports.map((report) => ({
      value: report,
      label: report.replace(/^PTR-|[^a-zA-Z\sıIğĞüÜşŞiİöÖçÇ]/g, ""),
    }));
  };

  const compareReducer = useSelector((state) => state[`Compare${index}`]);

  const {
    loading: branchesLoading,
    error: branchesError,
    branches,
    success: branchesSuccess,
  } = compareReducer.getBranches;

  const { reports, success: reportsSuccess } = compareReducer.getReports;
  const {
    loading: dataLoading,
    success: dataSuccess,
    report: data,
    error: dataError,
  } = compareReducer.getReportDetails;

  useEffect(() => {
    dispatch(getAllBranches({ userId: auth.user_id }));
  }, [auth.user_id, dispatch, getAllBranches]);

  useEffect(() => {
    if (branchesSuccess) {
      setSelectedBranch(formatBranches(branches)[0]);
    }
  }, [branchesSuccess, branches]);

  useEffect(() => {
    if (selectedBranch) {
      dispatch(getAllReports({ branchUrl: selectedBranch.value }));
    }
  }, [selectedBranch, dispatch, getAllReports]);

  useEffect(() => {
    if (reportsSuccess) {
      setSelectedReport(formatReports(reports)[0]);
    } else {
      setSelectedReport(null);
    }
  }, [reportsSuccess, reports]);

  const onSubmit = () => {
    if (!selectedBranch || !selectedReport) {
      return;
    }
    dispatch(
      getReportDetails({
        branchUrl: selectedBranch.value,
        reportId: selectedReport.value,
        start_date: startDate ? formatDate(startDate) : null,
        end_date: endDate ? formatDate(endDate) : null,
      })
    );
  };

  const filteredTables = filterReportTable(data, "");
  const isTableEmpty = isEmpty(filteredTables);

  useEffect(() => {
    if (dataSuccess && index === 1) {
      setDataLeft(data);
      setLeftBranch(selectedBranch?.label);
    } else if (dataSuccess && index === 2) {
      setDataRight(data);
      //console.log(selectedBranch)
      setRightBranch(selectedBranch?.label);
    }
  }, [
    dataSuccess,
    data,
    selectedBranch,
    index,
    setDataLeft,
    setDataRight,
    setLeftBranch,
    setRightBranch,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetGetBranches());
      dispatch(resetGetReports());
      dispatch(resetGetReportDetails());
    };
  }, [dispatch, resetGetBranches, resetGetReportDetails, resetGetReports]);

  useEffect(() => {
    if (dataSuccess) {
      setStartDate(formatToDate(data.startDate));
      setEndDate(formatToDate(data.endDate));
    }
  }, [data, dataSuccess]);

  return (
    <>
      {branchesLoading ? (
        <>
          <CompareSkeleton />
        </>
      ) : branchesError ? (
        <ErrorDisplay
          error={branchesError}
          onRetry={() => dispatch(getAllBranches({ userId: auth.user_id }))}
        />
      ) : branchesSuccess ? (
        <>
          <div>
            <div className="mb-2 bg-[--btn-txt] p-4 rounded-lg border-[.5px] border-solid border-[--gr-3] flex-1">
              <CompareSelect
                value={selectedBranch}
                options={formatBranches(branches)}
                onChange={setSelectedBranch}
                className="w-full p-2 mb-2 border-0 border-[--gr-3] border-solid rounded-md bg-[--btn-txt]"
                isDisabled={dataLoading}
              />

              <CustomSelect
                value={selectedReport}
                options={reports ? formatReports(reports) : []}
                onChange={setSelectedReport}
                className="w-full p-2 mb-2 border-0 border-[--gr-3] border-solid rounded-md bg-[--btn-txt]"
                isDisabled={dataLoading}
              />

              <div className="text-[14px] mb-1">{t("compare.choose")}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-4 mb-4 max-h-24 sm:max-h-8 gap-1">
                <DateTimePicker
                  value={startDate}
                  onChange={setStartDate}
                  placeholder={t("compare.startD")}
                  isDisabled={dataLoading}
                />
                <DateTimePicker
                  value={endDate}
                  onChange={setEndDate}
                  placeholder={t("compare.endD")}
                  isDisabled={dataLoading}
                />
              </div>
              <button
                onClick={onSubmit}
                disabled={dataLoading}
                className="w-full px-10 py-2 bg-[--primary] text-[--btn-txt] rounded-md focus:outline-none focus:ring-2 focus:ring-[--gr-5] focus:ring-offset-2 focus:ring-offset-gray-100 hover:bg-[--primary-hover]"
              >
                {dataLoading ? (
                  <div
                    role="status"
                    className="flex items-center justify-center"
                  >
                    <svg
                      aria-hidden="true"
                      className="w-5 h-5 text-[var(--tx-4)] animate-spin fill-blue-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  </div>
                ) : (
                  t("reports.create")
                )}
              </button>
            </div>
            <div>
              {dataSuccess ? (
                <>
                  {/*  
                 <div className="m-2">
                    <CustomInput
                      type="text"
                      placeholder="Search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full left-0 mb-2 p-3 rounded text-[--gr-4] text-sm border border-[--gr-2] border-solid cursor-text"
                    />
                  </div> 
                  */}

                  {filteredTables && !isTableEmpty ? (
                    filteredTables.map((table, index) => {
                      return (
                        table.rows.length > 0 && (
                          <MyTable
                            key={index}
                            classNames="my-2"
                            table={table}
                            showColumns={true}
                            showTitle={true}
                          />
                        )
                      );
                    })
                  ) : (
                    <ErrorDisplay error="No Results Found" />
                  )}
                </>
              ) : dataLoading ? (
                <>
                  <TableSkeleton />
                  <TableSkeleton />
                </>
              ) : dataError ? (
                <ErrorDisplay
                  error={dataError}
                  onRetry={() =>
                    getReportDetails({
                      branchUrl: selectedBranch.value,
                      reportId: selectedReport.value,
                      start_date: startDate ? formatDate(startDate) : null,
                      end_date: endDate ? formatDate(endDate) : null,
                    })
                  }
                />
              ) : null}
            </div>
          </div>
        </>
      ) : null}
    </>
  );
};

export default CompareForm;
